import React, { useEffect, useState } from "react"
import { Modal, Button, Checkbox } from "@mantine/core"
import InfiniteScroll from "react-infinite-scroller"
import { api } from "@/api"
import { SafedSearch } from "@/types/djangio"
import { useStyles } from "@/pages/campaign-form/components/sections/message/EditOfficials.styles"
import { Icon } from "@/components"
import debounce from "lodash.debounce"

interface OfficialItem {
    id: number
    name: string
    title: string
    image_url: string
}

interface ParamsObject {
    offset: number
    count: boolean
    exclude: object
    archived: boolean
    for_grassroots?: number
    current_searchify_selection?: string
    searchify_filters?: string
}

const hasMore = (pillGroup: SafedSearch, selectedOfficialsList: OfficialItem[]) => {
    if (!pillGroup) {
        return false
    }
    return selectedOfficialsList.length < pillGroup.estimated_size
}

const buildSearchParams = (obj) => {
    const paramsObj = new URLSearchParams()

    Object.entries(obj).forEach(([key, value]) => {
        if (value === null || value === undefined) return

        if (typeof value === "object") {
            paramsObj.append(key, Array.isArray(value) ? value.join(",") : JSON.stringify(value))
        } else {
            paramsObj.append(key, String(value))
        }
    })

    return paramsObj
}

export const EditOfficials = ({ pillGroup, defaultFilters, isOpen, onClose, onSearchifyEdit }) => {
    const { classes } = useStyles()
    const [getSelectedItems, setSelectedItems] = useState<OfficialItem[]>([])
    const [getSelectedOffset, setSelectedOffset] = useState<number>(0)
    const [getCount, setCount] = useState<number>(0)

    const getModalTitle = () => {
        return `${getCount} officials`
    }

    const getSelectedOfficials = async (addedIds: number[], deletedIds: number[], offset = 0) => {
        const filters = pillGroup.filters ?? []
        const paramsObj: ParamsObject = {
            ...defaultFilters,
            offset,
            count: false,
            exclude: {},
        }

        // Merge filter key-value pairs into paramsObj
        filters.forEach((filter) => Object.assign(paramsObj, filter))

        if (filters.length && filters[0].custom_official && paramsObj.for_grassroots) {
            delete paramsObj.for_grassroots
        }

        const querysetList = paramsObj
        const currentSearchify = JSON.stringify({
            addedIds,
            deletedIds,
            querysetList: filters.length ? [querysetList] : [],
            searchifyCount: pillGroup.estimated_size,
        })

        paramsObj.current_searchify_selection = currentSearchify
        paramsObj.searchify_filters = currentSearchify

        const response = await api.get({
            model: DjangIO.app.person.models.NewPerson,
            params: buildSearchParams(paramsObj).toString(),
        })

        return response.json()
    }

    const fetchSelectedOfficials = async (addedIds: number[], deletedIds: number[], offset = 0) => {
        const data = await getSelectedOfficials(addedIds, deletedIds, offset)
        setSelectedItems([...getSelectedItems, ...data.objects])
        setSelectedOffset(offset + data.meta.limit)
    }

    const handleOfficial = (id: number, isChecked: boolean): void => {
        if (isChecked) {
            pillGroup.added_ids.push(id)
            if (pillGroup.deleted_ids.includes(id)) {
                pillGroup.deleted_ids = pillGroup.deleted_ids.filter((deleted_id) => deleted_id !== id)
            }
        } else {
            pillGroup.deleted_ids.push(id)
            if (pillGroup.added_ids.includes(id)) {
                pillGroup.added_ids = pillGroup.added_ids.filter((added_id) => added_id !== id)
            }
        }
    }

    useEffect(() => {
        setSelectedItems([])
        setSelectedOffset(0)
        if (isOpen) {
            setCount(pillGroup && pillGroup.estimated_size ? pillGroup.estimated_size : 0)
            fetchSelectedOfficials(pillGroup.added_ids, pillGroup.deleted_ids, getSelectedOffset)
        }
    }, [isOpen])

    const renderSelectedItems = () => {
        const items = []

        if (!getSelectedItems) {
            return <div className="text-center">No results</div>
        }

        getSelectedItems.map((item) => {
            items.push(
                <div key={item.id} className={classes.officialItem}>
                    <div className="image">
                        <img src={item.image_url} alt={item.name} />
                    </div>
                    <div className="name">{item.name}</div>
                    <div className="title">{item.title}</div>
                    <div className={classes.officialCheckbox}>
                        <Checkbox
                            defaultChecked
                            onChange={(event) => handleOfficial(item.id, event.currentTarget.checked)}
                        />
                    </div>
                </div>,
            )
        })

        return items
    }

    return (
        <>
            <Modal
                opened={isOpen}
                onClose={() => onClose(pillGroup)}
                title={getModalTitle()}
                size="640px"
                className={classes.officialSelectModal}
                centered
            >
                <div className={classes.officialListWrapper}>
                    <InfiniteScroll
                        pageStart={0}
                        loadMore={debounce(
                            () => fetchSelectedOfficials(pillGroup.added_ids, pillGroup.deleted_ids, getSelectedOffset),
                            1000,
                        )}
                        initialLoad={false}
                        hasMore={hasMore(pillGroup, getSelectedItems)}
                        useWindow={false}
                        loader={
                            <div className="loader d-flex justify-content-center">
                                <div className="spinner-border" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </div>
                        }
                    >
                        <div className={classes.officialList}>{renderSelectedItems()}</div>
                    </InfiniteScroll>
                </div>
                <div className={classes.officialListFooter}>
                    <Button
                        className={classes.textButton}
                        leftIcon={<Icon icon="plus" />}
                        variant="tonal"
                        color="white"
                        size="md"
                        onClick={() => onSearchifyEdit(pillGroup)}
                    >
                        Add more officials
                    </Button>
                </div>
            </Modal>
        </>
    )
}
